import React, { useState } from 'react';
import { PiWarningCircleFill } from "react-icons/pi";
import { FaSitemap } from "react-icons/fa6";
import DeviationGauge from '../deviation-gauge';
import Position from '../../../api/models/portfolioPosition';
import { useSelector } from 'react-redux';
import { selectPortfolios } from '../portfolios-slice';
import PortfolioComposite from '../../../api/models/portfolioComposite';
import { componentIdealPercentageString, positionDescription } from '../../../app/utils';

interface PortfolioDetailPositionCardProps {
  position: Position
}

/**
 * A card to present the informations of a portfolio position.
 */
const PortfolioDetailPositionCard: React.FC<PortfolioDetailPositionCardProps> = (props: PortfolioDetailPositionCardProps) => {

  const [expanded, setExpanded] = useState(false);
  const portfolios = useSelector(selectPortfolios);

  function expandHandler(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    setExpanded(!expanded);
  }

  const pathCategoryNames = (!expanded || !props.position.id) ? [] : portfolios[0].portfolioComposite?.getCategoryPath(props.position.id);
  const categoryPath = pathCategoryNames?.slice(0, pathCategoryNames?.length - 1).join(' > ');
  const categoryName = (!props.position.parentCompositeId) ? undefined : (portfolios[0].portfolioComposite?.getComponentById(props.position.parentCompositeId) as PortfolioComposite).category.name;
  const messageRebalanceNeed =
    (props.position.absoluteDeviation() > 2 / 3 * 0.3 ? 'PRECISA ' : props.position.absoluteDeviation() > 1 / 3 * 0.3 ? 'DEVERIA ' : '')
    + (props.position.deviation() < 0 ? 'COMPRAR' : 'VENDER');
  const valueRebalanceNeed = Math.abs(props.position.deviation() * props.position.idealNotional()).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div onClick={expandHandler} className={`${expanded ? 'row-span-2 col-span-2' : ''} relative cursor-pointer flex w-full aspect-square items-center justify-center select-none ${expanded ? 'text-xs lg:text-base' : 'text-xs'} ${expanded ? 'lg:text-xl font-normal' : 'lg:text-sm font-light'}`}>
      <div className={`${expanded ? 'bg-white/30' : 'bg-white/10'} flex flex-col w-full h-full rounded-lg items-center justify-center overflow-hidden text-neutral-200 border lg:border-2 border-blue-300/40 shadow-lg shadow-slate-900/80 hover:bg-white/30`}>

        <div className='flex w-full px-1 lg:px-2 py-1 text-center items-center border-b lg:border-b-2 border-blue-300/40 bg-slate-800/90'>
          <div className='grow flex text-start overflow-hidden'>
            <label className='w-full text-nowrap [mask-image:linear-gradient(to_right,black_0,black_calc(100%-15px),transparent_100%)]'>{positionDescription(props.position)}</label>
          </div>
          <div className='h-4/5 mx-2 border-l lg:border-l-2 border-blue-300/40'></div>
          <div className='flex' title='Peso da posição'>
            <div className='text-end'>{componentIdealPercentageString(props.position)}</div>
          </div>
        </div>

        <div className='relative flex flex-row w-full h-full'>
          {!expanded && Math.abs(props.position.deviation()) >= 0.3 && <PiWarningCircleFill size={100} className='absolute top-0 left-0 bottom-0 right-0 self-center justify-self-center text-black/20' />}

          <div className='h-full flex flex-col w-3/5 items-start pl-2'>
            <div className={`grow flex flex-col pl-2 pt-2 items-start drop-shadow-[0_2px_2px_rgba(50,50,50,0.8)] ${props.position.deviation() < 0 ? 'text-green-400/90' : 'text-red-400/90'}`}>
              {expanded && <div className='text-sm lg:text-lg font-bold mt-1'>{messageRebalanceNeed}</div>}
              {expanded && <div className='text-sm lg:text-lg font-bold mt-1'>R$ {valueRebalanceNeed}</div>}
            </div>
            {expanded && <div className='lg:text-sm mt-1 border-b'>Quantidade</div>}
            {expanded && <label className='mb-1 lg:mb-2'>{props.position.quantity.toLocaleString('pt-BR')}</label>}
            {expanded && <div className='lg:text-sm mt-1 border-b'>Preço ({props.position.priceDate.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(', 00:00', '')})</div>}
            {expanded && <label className='mb-1 lg:mb-2'>R$ {props.position.price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>}
            <div className='lg:text-sm mt-1 border-b'>Valor ATUAL</div>
            <label className='mb-1 lg:mb-2'>R$ {props.position.notional().toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>
            {expanded && <div className='lg:text-sm mt-1 border-b'>Valor IDEAL</div>}
            {expanded && <label className='mb-1 lg:mb-2'>R$ {props.position.idealNotional().toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>}
          </div>

          <div className='h-full flex flex-col w-2/5 items-end text-end justify-start pr-2'>
            <DeviationGauge
              deviation={props.position.deviation()}
              deviationLimit={0.3}
              animate={false}
              className={`${expanded ? 'mt-5 lg:mt-10x' : 'mt-3 lg:mt-5'} w-4/5 flex items-center justify-center`}
              valueClassName='text-center w-full mt-2 bg-transparent'
            />
            {expanded && <div className='w-full text-center lg:text-base'>(desvio)</div>}
            <div className='grow'></div>
            {expanded && <div className='lg:text-sm mt-1 border-b'>% do Portfolio ATUAL</div>}
            {expanded && <label className='mb-1 lg:mb-2'>{(props.position.currentPercentage() * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</label>}
            {expanded && <div className='lg:text-sm mt-1 border-b'>% do Portfolio IDEAL</div>}
            {expanded && <label className='mb-1 lg:mb-2'>{(props.position.idealPercentage() * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</label>}
          </div>

        </div>


        <div className='flex w-full px-1 border-blue-300/40 bg-slate-800/90 max-h-10 lg:max-h-14'>
          <div className='grow' />
          <div className='flex flex-row items-center' title='Categoria da posição'>
            {expanded && categoryPath && <div className='text-xs text-end'>{categoryPath + ' >'}</div>}
            {categoryName && <div title={categoryName} className={`${expanded ? 'text-sm lg:text-md' : 'text-xs'} lg:ml-2 mr-2 text-end`}>{expanded ? categoryName : categoryName.slice(0, 15) + (categoryName.length > 15 ? '..' : '')}</div>}
            <FaSitemap />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PortfolioDetailPositionCard;